// DTO de C1 — dashboard del coordinador.
//
//   - GET /api/coordinador/dashboard?cadencia=hoy|semana|mes
//
// Refactor 2026-05-26: refactor visual completo alineado al diseño piloto UPG.
// Cambios breaking respecto a la versión anterior:
//   - Cadencia toggle hoy/semana/mes
//   - KPIs específicos: cumpl Alpina + cumpl Meta Interna + Producido + Paros (con sparklines)
//   - Líneas extendidas con líder, gap, paros del día
//   - Sección atención con discriminated union (3 tipos)
//   - Eventos derivados (Esta semana)
//   - Acceso rápido con metadata viva
//   - Tendencia 7 días (3 series)
//
// Las dos métricas de cumplimiento son distintas:
//   - cumpl_programa_cliente: cantidad_real / cantidad_programada_alpina
//     (basado en vw_cumplimiento_dia_sku_linea)
//   - cumpl_meta_interna: cantidad_real / (capacidad_estandar_unidades_hora × bloques)
//     (basado en vw_kpis_linea_turno — capacidad editable por el coordinador en C10)
import { z } from 'zod';
import { conectividadLecturaSchema } from './ingesta_maquina.js';
const semanaIsoRegex = /^[0-9]{4}-W[0-9]{2}$/;
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
// =============================================================================
// Cadencia
// =============================================================================
export const cadenciaSchema = z.enum(['hoy', 'semana', 'mes']);
export const coordinadorDashboardQuerySchema = z.object({
    cadencia: cadenciaSchema.optional(),
    /**
     * Rango explícito (opcional). Si vienen AMBOS `desde` y `hasta`, el dashboard
     * usa ese rango literal en vez de la cadencia. Para `global_periodo` el
     * "período completo" pasa a ser el rango dado tal cual (desde→hasta).
     */
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    /**
     * Filtro opcional por planta (drill del directivo / coordinador
     * multi-planta). Se INTERSECTA con el scope visible: una planta fuera del
     * scope simplemente devuelve agregados vacíos. `scope.plantas_asignadas`
     * SIEMPRE lista todas las visibles (alimenta el selector del FE).
     */
    planta_id: z.string().uuid().optional(),
});
// =============================================================================
// Sparkline (7 puntos diarios — independiente de cadencia)
// =============================================================================
export const sparklinePuntoSchema = z.object({
    fecha: z.string().date(),
    valor: z.number().nullable(),
});
// =============================================================================
// KPIs (4 cards del header)
// =============================================================================
const kpiCumplSchema = z.object({
    /** Valor del periodo según cadencia (0-100). null si no calculable. */
    valor_pct: z.number().min(0).nullable(),
    /** Delta en puntos porcentuales vs periodo anterior (ayer/semana ant/mes ant). null si no comparable. */
    delta_pp: z.number().nullable(),
    /** 7 puntos diarios (lo último). */
    sparkline: z.array(sparklinePuntoSchema).max(7),
});
/**
 * Meta interna = cantidad_real / capacidad_esperada (capacidad instalada del
 * período). Extiende el KPI de cumplimiento con los absolutos para mostrar
 * "vs X esperado" en la card (análogo a producción con su "vs X programado").
 */
const kpiMetaInternaSchema = kpiCumplSchema.extend({
    /** Producido real del período (numerador). null si no calculable. */
    cantidad_real: z.number().min(0).nullable(),
    /** Capacidad esperada del período (denominador). null si no calculable. */
    cantidad_esperada: z.number().min(0).nullable(),
});
const kpiProduccionSchema = z.object({
    cantidad: z.number().min(0),
    cantidad_programada: z.number().min(0).nullable(),
    sparkline: z.array(sparklinePuntoSchema).max(7),
    /**
     * A6 `pendiente_validacion` (diccionario v1, política 1.1): cuánto del
     * producido del período aún NO tiene validación de supervisor. Es lo que
     * reconcilia la vista de operación (capturado) con el informe (validado)
     * sin convertir nada a "solo validado". null cuando no hay producción.
     */
    pendiente_validacion: z
        .object({
        unidades: z.number().min(0),
        /** % sobre `cantidad` (1 decimal). */
        pct: z.number().min(0).max(100),
    })
        .nullable()
        .optional(),
});
/**
 * "Paro que afecta disponibilidad" (diccionario B1). minutos_total y
 * eventos_count son SOLO de paros CERRADOS; los abiertos van aparte (DN1).
 */
const kpiParosSchema = z.object({
    minutos_total: z.number().min(0),
    eventos_count: z.number().int().min(0),
    /** Paros abiertos (sin hora_fin) en el rango/scope — se muestran aparte, no suman. */
    en_curso: z.number().int().min(0).optional(),
    sparkline: z.array(sparklinePuntoSchema).max(7),
});
export const kpisDashboardSchema = z.object({
    cumpl_programa_cliente: kpiCumplSchema,
    cumpl_meta_interna: kpiMetaInternaSchema,
    produccion: kpiProduccionSchema,
    paros: kpiParosSchema,
});
// =============================================================================
// Narrativa del header
// =============================================================================
export const narrativaSchema = z.object({
    /** Primer nombre del usuario (no full name). */
    saludo: z.string(),
    /** Primera planta del scope (para "Sopó · ..."). null si scope vacío. */
    planta_principal: z.string().nullable(),
    /** "Turno N en curso" si turno_actual_id != null, sino null. */
    turno_label: z.string().nullable(),
    /** Cantidad acumulada HOY (independiente de la cadencia seleccionada). */
    cantidad_hoy: z.number().min(0),
    /** Cumpl. Alpina HOY (independiente de cadencia). */
    cumpl_hoy_pct: z.number().min(0).nullable(),
});
// =============================================================================
// Línea en la tabla (estado del turno actual + paros del día)
// =============================================================================
export const lineaTablaEstadoSchema = z.enum([
    'verde',
    'amarillo',
    'rojo',
    'sin_data',
    'sin_captura',
    'paro',
]);
export const lineaTablaSchema = z.object({
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    /** Agrupación/área de la línea (subtítulo de la tabla). null si no asignada. */
    area_nombre: z.string().nullable(),
    /** Líder asignado al turno actual (programaciones_lideres vigente). null si no asignado o agregación por período. */
    lider_turno_nombre: z.string().nullable(),
    turno_numero: z.number().int().min(1).max(4).nullable(),
    estado_semaforo: lineaTablaEstadoSchema,
    /** Cumplimiento contra meta interna (capacidad) en el PERÍODO de la cadencia. null si sin captura. */
    cumplimiento_pct: z.number().min(0).nullable(),
    /** Gap = cantidad_real - meta_unidades (negativo = falta producir). null si no calculable. */
    gap_unidades: z.number().nullable(),
    /** Cantidad real producida en el período. */
    cantidad_real: z.number().min(0),
    /** Meta interna (capacidad) acumulada del período. null si no calculable. */
    meta_unidades: z.number().min(0).nullable(),
    // NOTA WS-4: a nivel LÍNEA no se expone cumplimiento vs programa ni cantidad
    // programada — el programa se mide por ÁREA (la producción se mueve entre líneas
    // de un proceso). El cumplimiento vs programa vive en C5 (agrupación por área).
    /** Paros del período. */
    paros_count: z.number().int().min(0),
    paros_minutos: z.number().min(0),
    paro_activo: z.boolean(),
    /** Ingesta de máquina (opcional): null en líneas manuales. */
    ...conectividadLecturaSchema.shape,
});
// =============================================================================
// Atención (discriminated union — 3 tipos)
// =============================================================================
const atencionBase = {
    id: z.string(),
    cta_label: z.string(),
    cta_link: z.string(),
};
export const atencionProgramacionNuevaSchema = z.object({
    tipo: z.literal('programacion_alpina_nueva'),
    ...atencionBase,
    titulo: z.string(),
    detalle: z.string(),
    version_numero: z.number().int().min(1),
    fecha_carga_iso: z.string().datetime().nullable(),
});
export const atencionParosRecurrentesSchema = z.object({
    tipo: z.literal('paros_recurrentes'),
    ...atencionBase,
    titulo: z.string(),
    detalle: z.string(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    causal_madre_nombre: z.string(),
    paros_count: z.number().int().min(3),
});
export const atencionLiderSinAsignarSchema = z.object({
    tipo: z.literal('lider_sin_asignar'),
    ...atencionBase,
    titulo: z.string(),
    detalle: z.string(),
    linea_id: z.string().uuid(),
    fecha_objetivo: z.string().date(),
    turno_numero: z.number().int().min(1).max(4),
});
export const atencionItemSchema = z.discriminatedUnion('tipo', [
    atencionProgramacionNuevaSchema,
    atencionParosRecurrentesSchema,
    atencionLiderSinAsignarSchema,
]);
// =============================================================================
// Eventos de la semana (derivados — Opción A acordada con el responsable)
// =============================================================================
export const eventoSemanaTipoSchema = z.enum([
    'reunion_directivos',
    'cierre_programacion',
    'inicio_semana',
]);
export const eventoSemanaSchema = z.object({
    id: z.string(),
    fecha: z.string().date(),
    hora_hhmm: z.string().regex(horaTimeRegex).nullable(),
    titulo: z.string(),
    detalle: z.string().nullable(),
    tipo: eventoSemanaTipoSchema,
});
// =============================================================================
// Acceso rápido (4 cards horizontales)
// =============================================================================
export const accesoRapidoSchema = z.object({
    programar_proxima_semana: z.object({
        semana_iso: z.string().regex(semanaIsoRegex),
        lunes: z.string().date(),
        domingo: z.string().date(),
        lineas_total: z.number().int().min(0),
        /** true si NO hay programación cargada para esa semana (badge PENDIENTE). */
        pendiente: z.boolean(),
    }),
    resumen_semana_anterior: z.object({
        semana_iso: z.string().regex(semanaIsoRegex),
        /** Si tenemos data de cumplimiento agregado. Útil para deshabilitar el card si no hay nada. */
        tiene_data: z.boolean(),
    }),
    programacion_cliente_ultima: z
        .object({
        version_numero: z.number().int().min(1),
        fecha_carga_iso: z.string().datetime().nullable(),
        pendiente_revision: z.boolean(),
    })
        .nullable(),
    analisis_mes: z.object({
        /** "May 2026" en es-CO. */
        mes_label: z.string(),
        dias_con_data: z.number().int().min(0),
    }),
});
// =============================================================================
// Tendencia 7 días (3 series, mismas fechas)
// =============================================================================
export const tendencia7DiasSchema = z.object({
    fechas: z.array(z.string().date()).max(7),
    /** Cumplimiento contra meta interna por día (0-100 o null). */
    cumplimiento_pct: z.array(z.number().nullable()).max(7),
    /** Producción total en unidades por día. */
    produccion: z.array(z.number().nullable()).max(7),
    /** Minutos de paro por día. */
    paros_minutos: z.array(z.number().nullable()).max(7),
});
// =============================================================================
// Global del período — la métrica CONFIABLE de cumplimiento de programa
// =============================================================================
//
// El KPI `cumpl_programa_cliente` se calcula sobre vw_cumplimiento_dia_sku_linea,
// que SOLO materializa combinaciones (fecha, línea, SKU) que tuvieron captura —
// por eso su "programado" está SUBESTIMADO. `global_periodo` resuelve eso
// tomando el programa de la FUENTE REAL (programaciones_detalle de la versión
// vigente) sobre el período COMPLETO, no solo lo capturado.
//
// Distinción clave de ventanas temporales:
//   - programado_total → período COMPLETO (ej. semana lun→dom entera, mes entero)
//   - producido_*       → solo el rango TRANSCURRIDO (hasta hoy)
// Por eso avance_pct = "% del plan del período que ya llevo".
export const globalPeriodoSchema = z.object({
    /** SUM(programaciones_detalle.cantidad_paquetes) del PERÍODO COMPLETO (plan real). */
    programado_total: z.number().min(0),
    /** Producido total en el rango TRANSCURRIDO (todas las combinaciones, en plan o no). */
    producido_total: z.number().min(0),
    /** Producido en combinaciones (línea, SKU) que SÍ están en el plan del período (match ignora el día). */
    producido_en_plan: z.number().min(0),
    /** Producido en líneas/SKU que NO están en el plan del período (indicador de desalineación). */
    producido_fuera_plan: z.number().min(0),
    /** producido_total / programado_total × 100. null si programado_total = 0. */
    avance_pct: z.number().min(0).nullable(),
    /** Inicio del rango COMPLETO usado para el programa (para aclaración en el FE). */
    periodo_desde: z.string().date(),
    /** Fin del rango COMPLETO usado para el programa. */
    periodo_hasta: z.string().date(),
});
// =============================================================================
// Response GET /api/coordinador/dashboard
// =============================================================================
export const coordinadorDashboardSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    }),
    fecha_hoy: z.string().date(),
    hora_servidor: z.string().regex(horaTimeRegex),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Turno corriendo AHORA (hora_servidor en tz tenant). null = fuera de turno. */
    turno_actual_id: z.string().uuid().nullable(),
    /** Cadencia activa (lo que el usuario eligió en el toggle). */
    cadencia: cadenciaSchema,
    narrativa: narrativaSchema,
    /** KPIs según cadencia. Sparklines siempre 7 días recientes. */
    kpis: kpisDashboardSchema,
    /**
     * Métrica global confiable del período: programado real (período completo) vs
     * producido (transcurrido), con discriminación de producción fuera de plan.
     * Resuelve la subestimación del KPI `cumpl_programa_cliente`.
     */
    global_periodo: globalPeriodoSchema,
    /** Líneas del scope (orden por prioridad operativa). */
    lineas: z.array(lineaTablaSchema),
    /** Items que requieren atención del coordinador. Cap 20. */
    atencion: z.array(atencionItemSchema),
    /** Eventos derivados de la semana actual + próxima. */
    eventos_semana: z.array(eventoSemanaSchema),
    acceso_rapido: accesoRapidoSchema,
    tendencia_7_dias: tendencia7DiasSchema,
});
//# sourceMappingURL=coordinador_dashboard.js.map